import gqlDataProvider, { generateFilter, generateSort, GraphQLClient } from '@refinedev/graphql';
import camelCase from 'camelcase';
import * as gql from 'gql-query-builder';
import { BaseRecord, DataProvider, GetListParams } from '@refinedev/core';
import { auth } from './firebase';
import pluralize from 'pluralize';

const API_URL = `${import.meta.env.VITE_API_URL}/admin/graphql`;

const client = new GraphQLClient(API_URL, {
  requestMiddleware: async (request) => {
    const token = await auth.currentUser?.getIdToken();
    request.headers = {
      ...request.headers,
      Authorization: `Bearer ${token}`,
    };
    return request;
  },
});

const baseDataProvider = gqlDataProvider(client);
export const dataProvider: DataProvider = {
  ...baseDataProvider,
  getList: async ({ resource, pagination, sorters, filters, meta }: GetListParams) => {
    const { current = 1, pageSize = 10, mode = 'server' } = pagination ?? {};

    const sortBy = generateSort(sorters);
    const filterBy = generateFilter(filters?.filter((f) => !!f.value));

    const camelResource = camelCase(resource);

    const operation = meta?.operation ?? camelResource;

    const { query, variables } = gql.query({
      operation,
      variables: {
        ...meta?.variables,
        sort: sortBy,
        where: { value: filterBy, type: 'JSON' },
        ...(mode === 'server'
          ? {
              after: ((current - 1) * pageSize).toString(),
              first: pageSize,
            }
          : {}),
      },
      fields: ['count', { nodes: meta?.fields }],
    });

    const response = await client.request<BaseRecord>(query, variables);

    return {
      data: response[operation].nodes,
      total: response[operation].count,
    };
  },
  getMany: async ({ resource, ids, meta }) => {
    const camelResource = camelCase(resource);

    const operation = meta?.operation ?? camelResource;

    const { query, variables } = gql.query({
      operation,
      variables: {
        where: {
          value: { id_in: ids },
          type: 'JSON',
        },
      },
      fields: [{ nodes: meta?.fields }],
    });

    const response = await client.request<BaseRecord>(query, variables);

    return {
      data: response[operation].nodes,
    };
  },
  create: async ({ resource, variables, meta }) => {
    const singularResource = pluralize.singular(resource);
    const camelCreateName = camelCase(`create-${singularResource}`);
    const pascalCreateName = camelCase(`create-${singularResource}`, { pascalCase: true });

    const operation = meta?.operation ?? camelCreateName;
    const inputType = meta?.inputType ?? `${pascalCreateName}Input!`;

    const { query, variables: gqlVariables } = gql.mutation({
      operation,
      variables: {
        input: {
          value: { data: variables },
          type: inputType,
        },
      },
      fields: meta?.fields ?? [
        {
          operation: singularResource,
          fields: ['id'],
          variables: {},
        },
      ],
    });
    const response = await client.request<BaseRecord>(query, gqlVariables);

    return {
      data: response[operation][singularResource],
    };
  },
  update: async ({ resource, id, variables, meta }) => {
    const singularResource = pluralize.singular(resource);
    const camelUpdateName = camelCase(`update-${singularResource}`);
    const pascalUpdateName = camelCase(`update-${singularResource}`, { pascalCase: true });

    const operation = meta?.operation ?? camelUpdateName;
    const inputType = meta?.operation ?? `${pascalUpdateName}Input!`;

    const { query, variables: gqlVariables } = gql.mutation({
      operation,
      variables: {
        input: {
          value: { where: { id }, data: variables },
          type: inputType,
        },
      },
      fields: meta?.fields ?? [
        {
          operation: singularResource,
          fields: ['id'],
          variables: {},
        },
      ],
    });
    const response = await client.request<BaseRecord>(query, gqlVariables);

    return {
      data: response[operation][singularResource],
    };
  },
  deleteOne: async ({ resource, id, meta }) => {
    const singularResource = pluralize.singular(resource);
    const camelDeleteName = camelCase(`delete-${singularResource}`);
    const pascalDeleteName = camelCase(`delete-${singularResource}`, { pascalCase: true });

    const operation = meta?.operation ?? camelDeleteName;
    const inputType = meta?.operation ?? `${pascalDeleteName}Input!`;

    const { query, variables } = gql.mutation({
      operation,
      variables: {
        input: {
          value: { where: { id } },
          type: inputType,
        },
      },
      fields: meta?.fields ?? [
        {
          operation: singularResource,
          fields: ['id'],
          variables: {},
        },
      ],
    });

    const response = await client.request<BaseRecord>(query, variables);

    return {
      data: response[operation][singularResource],
    };
  },
  custom: async ({ url, method, headers, meta }) => {
    let gqlClient = client;

    if (url) {
      gqlClient = new GraphQLClient(url, { headers });
    }

    if (meta) {
      if (meta.operation) {
        if (method === 'get') {
          const { query, variables } = gql.query({
            operation: meta.operation,
            fields: meta.fields,
            variables: meta.variables,
          });

          const response = await gqlClient.request<BaseRecord>(query, variables);

          return {
            data: response[meta.operation],
          };
        } else {
          const { query, variables } = gql.mutation({
            operation: meta.operation,
            fields: meta.fields,
            variables: meta.variables,
          });

          const response = await gqlClient.request<BaseRecord>(query, variables);

          return {
            data: response[meta.operation],
          };
        }
      } else {
        throw Error('GraphQL operation name required.');
      }
    } else {
      throw Error('GraphQL need to operation, fields and variables values in meta object.');
    }
  },
};
